"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { AgentSearchCard } from "./agent-search-card";
import type { AgentSearchResult } from "@/types";

const DEBOUNCE_MS = 100;

/**
 * Catalog item for local search - can be agent or skill
 */
export interface CatalogItem {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  category?: string;
  tags?: string[];
  icon?: string;
  /** Type of item - 'agent' or 'skill' */
  type: 'agent' | 'skill';
}

export interface AgentSearchDropdownProps {
  query: string;
  onSelect: (agent: AgentSearchResult) => void;
  onClose: () => void;
  onSubmitWithoutSelect: () => void;
  className?: string;
  /** Local catalog to search (replaces API-based searchEndpoint) */
  catalog?: CatalogItem[];
  /** @deprecated Use catalog prop instead */
  searchEndpoint?: string;
}

/**
 * Fuzzy match score - returns 0-1 based on match quality
 */
function fuzzyMatch(text: string, query: string): number {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  // Exact match
  if (lowerText === lowerQuery) return 1.0;

  // Starts with query
  if (lowerText.startsWith(lowerQuery)) return 0.9;

  // Contains query
  if (lowerText.includes(lowerQuery)) return 0.7;

  // Word boundary match
  const words = lowerText.split(/[-_\s]+/);
  for (const word of words) {
    if (word.startsWith(lowerQuery)) return 0.6;
  }

  // Character sequence match (fuzzy)
  let queryIdx = 0;
  let matches = 0;
  for (let i = 0; i < lowerText.length && queryIdx < lowerQuery.length; i++) {
    if (lowerText[i] === lowerQuery[queryIdx]) {
      matches++;
      queryIdx++;
    }
  }
  if (queryIdx === lowerQuery.length) {
    return 0.3 + (matches / lowerText.length) * 0.2;
  }

  return 0;
}

/**
 * Search catalog items locally
 */
function searchCatalog(catalog: CatalogItem[], query: string): AgentSearchResult[] {
  // Remove @ or # prefix for searching
  const searchQuery = query.replace(/^[@#]/, '').trim();

  if (!searchQuery) {
    // Show all items when just @ is typed
    return catalog.slice(0, 10).map(item => ({
      agent_definition_id: item.id,
      name: item.name,
      display_name: item.displayName,
      description: item.description || '',
      category: item.category,
      tags: item.tags || [],
      icon: item.icon,
      score: 1.0,
      match_type: 'keyword' as const,
    }));
  }

  // Score and filter items
  const scored = catalog.map(item => {
    const nameScore = fuzzyMatch(item.name, searchQuery);
    const displayNameScore = fuzzyMatch(item.displayName, searchQuery);
    const descScore = item.description ? fuzzyMatch(item.description, searchQuery) * 0.5 : 0;
    const tagScore = item.tags?.some(t => fuzzyMatch(t, searchQuery) > 0.5) ? 0.4 : 0;

    const score = Math.max(nameScore, displayNameScore, descScore, tagScore);

    return { item, score };
  });

  // Filter and sort by score
  return scored
    .filter(({ score }) => score > 0.2)
    .sort((a, b) => b.score - a.score)
    .slice(0, 10)
    .map(({ item, score }) => ({
      agent_definition_id: item.id,
      name: item.name,
      display_name: item.displayName,
      description: item.description || '',
      category: item.category,
      tags: item.tags || [],
      icon: item.icon,
      score,
      match_type: 'keyword' as const,
    }));
}

export function AgentSearchDropdown({
  query,
  onSelect,
  onClose,
  onSubmitWithoutSelect,
  className,
  catalog = [],
}: AgentSearchDropdownProps) {
  const [results, setResults] = useState<AgentSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced local search
  useEffect(() => {
    if (query.length < 1) {
      setResults([]);
      return;
    }

    const timer = setTimeout(() => {
      const searchResults = searchCatalog(catalog, query);
      setResults(searchResults);
      setSelectedIndex(0);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, catalog]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Only handle specific keys for dropdown navigation
    const handledKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];
    if (!handledKeys.includes(e.key)) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(i => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (results[selectedIndex]) {
          onSelect(results[selectedIndex]);
        } else {
          onSubmitWithoutSelect();
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        onClose();
        break;
      case 'Tab':
        // Close on tab to allow natural focus flow
        onClose();
        break;
    }
  }, [results, selectedIndex, onSelect, onSubmitWithoutSelect, onClose]);

  // Attach keyboard listener with capture phase to intercept before ComposerPrimitive
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true); // capture phase
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (containerRef.current && results.length > 0) {
      const selectedEl = containerRef.current.querySelector('[aria-selected="true"]');
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, results.length]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "absolute bottom-full left-0 right-0 mb-1 z-50",
        "bg-popover border border-border rounded-md shadow-lg",
        "max-h-[300px] overflow-y-auto",
        className
      )}
      role="listbox"
      aria-label="Search results"
    >
      {/* Results */}
      {results.map((agent, index) => (
        <AgentSearchCard
          key={agent.agent_definition_id}
          agent={agent}
          isSelected={index === selectedIndex}
          onClick={() => onSelect(agent)}
        />
      ))}

      {/* No results */}
      {results.length === 0 && (
        <div className="px-3 py-4 text-sm text-muted-foreground text-center">
          {catalog.length === 0
            ? "No skills available. Add agents to your repo to use @ mentions."
            : `No matches found for "${query.replace(/^[@#]/, '')}"`
          }
        </div>
      )}

      {/* Footer hint */}
      <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border/50 bg-muted/30">
        <span className="opacity-70">
          ↑↓ navigate • Enter select • Esc close
        </span>
      </div>
    </div>
  );
}
